/**
 * Authentication Token Manager
 * Handles secure storage, validation, and refresh of authentication tokens
 * Now with optional encryption support
 */

import { AuthMethod } from '../types';
import { TokenEncryptionService } from './TokenEncryptionService';

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  authMethod: AuthMethod;
  userId: string;
}

export interface TokenValidationResult {
  isValid: boolean;
  isExpired: boolean;
  needsRefresh: boolean;
  timeUntilExpiry?: number;
}

export class AuthTokenManager {
  private static instance: AuthTokenManager | null = null;
  private encryptionService: TokenEncryptionService | null = null;
  private useEncryption: boolean = false;
  
  private readonly ACCESS_TOKEN_KEY = 'playhub_access_token';
  private readonly REFRESH_TOKEN_KEY = 'playhub_refresh_token';
  private readonly TOKEN_EXPIRY_KEY = 'playhub_token_expiry';
  private readonly AUTH_METHOD_KEY = 'playhub_auth_method';
  private readonly USER_ID_KEY = 'playhub_user_id';
  
  // Token expiry settings
  private readonly ACCESS_TOKEN_LIFETIME = 60 * 60 * 1000; // 1 hour
  private readonly REFRESH_TOKEN_LIFETIME = 30 * 24 * 60 * 60 * 1000; // 30 days
  private readonly REFRESH_THRESHOLD = 5 * 60 * 1000; // Refresh if less than 5 minutes remaining

  static getInstance(): AuthTokenManager {
    if (!AuthTokenManager.instance) {
      AuthTokenManager.instance = new AuthTokenManager();
    }
    return AuthTokenManager.instance;
  }

  /**
   * Enable token encryption
   */
  async enableEncryption(): Promise<boolean> {
    try {
      this.encryptionService = TokenEncryptionService.getInstance();
      
      if (!this.encryptionService.isAvailable()) {
        console.warn('⚠️ Token encryption not available in this browser');
        return false;
      }
      
      await this.encryptionService.initialize();
      this.useEncryption = true;
      console.log('🔐 Token encryption enabled');
      return true;
    } catch (error) {
      console.error('Failed to enable token encryption:', error);
      return false;
    }
  }

  /**
   * Store authentication tokens securely
   */
  async storeTokens(tokens: AuthTokens): Promise<void> {
    try {
      let accessToken = tokens.accessToken;
      let refreshToken = tokens.refreshToken;
      
      // Encrypt tokens if encryption is enabled
      if (this.useEncryption && this.encryptionService) {
        accessToken = await this.encryptionService.encrypt(tokens.accessToken);
        refreshToken = await this.encryptionService.encrypt(tokens.refreshToken);
      }
      
      localStorage.setItem(this.ACCESS_TOKEN_KEY, accessToken);
      localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
      localStorage.setItem(this.TOKEN_EXPIRY_KEY, tokens.expiresAt.toString());
      localStorage.setItem(this.AUTH_METHOD_KEY, tokens.authMethod);
      localStorage.setItem(this.USER_ID_KEY, tokens.userId);
      
      console.log('🔐 Tokens stored successfully', {
        authMethod: tokens.authMethod,
        userId: tokens.userId,
        expiresAt: new Date(tokens.expiresAt).toISOString(),
        encrypted: this.useEncryption
      });
    } catch (error) {
      console.error('Failed to store authentication tokens:', error);
      throw new Error('Failed to store authentication tokens');
    }
  }

  /**
   * Retrieve stored authentication tokens
   */
  async getTokens(): Promise<AuthTokens | null> {
    try {
      let accessToken = localStorage.getItem(this.ACCESS_TOKEN_KEY);
      let refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY);
      const expiresAt = localStorage.getItem(this.TOKEN_EXPIRY_KEY);
      const authMethod = localStorage.getItem(this.AUTH_METHOD_KEY) as AuthMethod;
      const userId = localStorage.getItem(this.USER_ID_KEY);

      if (!accessToken || !refreshToken || !expiresAt || !authMethod || !userId) {
        return null;
      }

      // Decrypt tokens if encryption is enabled
      if (this.useEncryption && this.encryptionService) {
        try {
          accessToken = await this.encryptionService.decrypt(accessToken);
          refreshToken = await this.encryptionService.decrypt(refreshToken);
        } catch (error) {
          console.error('Failed to decrypt tokens:', error);
          // Clear corrupted tokens
          this.clearTokens();
          return null;
        }
      }

      return {
        accessToken,
        refreshToken,
        expiresAt: parseInt(expiresAt, 10),
        authMethod,
        userId
      };
    } catch (error) {
      console.error('Failed to retrieve authentication tokens:', error);
      return null;
    }
  }

  /**
   * Validate current tokens
   */
  async validateTokens(): Promise<TokenValidationResult> {
    const tokens = await this.getTokens();

    if (!tokens) {
      return {
        isValid: false,
        isExpired: true,
        needsRefresh: false
      };
    }

    const now = Date.now();
    const timeUntilExpiry = tokens.expiresAt - now;
    const isExpired = timeUntilExpiry <= 0;
    const needsRefresh = timeUntilExpiry <= this.REFRESH_THRESHOLD && timeUntilExpiry > 0;

    return {
      isValid: !isExpired,
      isExpired,
      needsRefresh,
      timeUntilExpiry: Math.max(0, timeUntilExpiry)
    };
  }

  /**
   * Check if tokens exist and are valid
   */
  async hasValidTokens(): Promise<boolean> {
    const validation = await this.validateTokens();
    return validation.isValid;
  }

  /**
   * Check if tokens need refresh
   */
  async shouldRefreshTokens(): Promise<boolean> {
    const validation = await this.validateTokens();
    return validation.needsRefresh;
  }

  /**
   * Generate new authentication tokens
   */
  generateTokens(authMethod: AuthMethod, userId: string): AuthTokens {
    const now = Date.now();
    
    return {
      accessToken: this.generateToken('access', authMethod, userId),
      refreshToken: this.generateToken('refresh', authMethod, userId),
      expiresAt: now + this.ACCESS_TOKEN_LIFETIME,
      authMethod,
      userId
    };
  }

  /**
   * Refresh access token using refresh token
   */
  async refreshAccessToken(): Promise<AuthTokens | null> {
    try {
      const currentTokens = await this.getTokens();
      
      if (!currentTokens) {
        console.warn('No tokens to refresh');
        return null;
      }

      // Check if refresh token itself is expired
      const refreshTokenExpiry = this.extractTokenExpiry(currentTokens.refreshToken);
      if (refreshTokenExpiry && Date.now() >= refreshTokenExpiry) {
        console.warn('Refresh token expired, full re-authentication required');
        this.clearTokens();
        return null;
      }

      console.log('🔄 Refreshing access token...');

      // Simulate token refresh (in production, this would call your backend)
      await new Promise(resolve => setTimeout(resolve, 500));

      // Generate new tokens
      const newTokens = this.generateTokens(
        currentTokens.authMethod,
        currentTokens.userId
      );

      // Store new tokens
      await this.storeTokens(newTokens);

      console.log('✅ Access token refreshed successfully');
      return newTokens;
    } catch (error) {
      console.error('Failed to refresh access token:', error);
      return null;
    }
  }

  /**
   * Clear all stored tokens (logout)
   */
  clearTokens(): void {
    try {
      localStorage.removeItem(this.ACCESS_TOKEN_KEY);
      localStorage.removeItem(this.REFRESH_TOKEN_KEY);
      localStorage.removeItem(this.TOKEN_EXPIRY_KEY);
      localStorage.removeItem(this.AUTH_METHOD_KEY);
      localStorage.removeItem(this.USER_ID_KEY);
      
      // Clear encryption key
      if (this.encryptionService) {
        this.encryptionService.clearKey();
      }
      
      console.log('🔓 Tokens cleared successfully');
    } catch (error) {
      console.error('Failed to clear authentication tokens:', error);
    }
  }

  /**
   * Get time until token expiry in milliseconds
   */
  async getTimeUntilExpiry(): Promise<number> {
    const tokens = await this.getTokens();
    if (!tokens) return 0;
    
    return Math.max(0, tokens.expiresAt - Date.now());
  }

  /**
   * Get time until token expiry in human-readable format
   */
  async getTimeUntilExpiryFormatted(): Promise<string> {
    const ms = await this.getTimeUntilExpiry();
    
    if (ms === 0) return 'Expired';
    
    const minutes = Math.floor(ms / (60 * 1000));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days} day${days > 1 ? 's' : ''}`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''}`;
    if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''}`;
    
    return 'Less than a minute';
  }

  /**
   * Schedule automatic token refresh
   */
  async scheduleTokenRefresh(callback: () => void): Promise<NodeJS.Timeout | null> {
    const validation = await this.validateTokens();
    
    if (!validation.isValid) {
      return null;
    }

    // Schedule refresh 5 minutes before expiry
    const refreshTime = Math.max(0, (validation.timeUntilExpiry || 0) - this.REFRESH_THRESHOLD);
    
    console.log(`⏰ Token refresh scheduled in ${Math.round(refreshTime / 1000 / 60)} minutes`);
    
    return setTimeout(() => {
      console.log('⏰ Automatic token refresh triggered');
      callback();
    }, refreshTime);
  }

  /**
   * Get authentication method from stored tokens
   */
  async getStoredAuthMethod(): Promise<AuthMethod | null> {
    const tokens = await this.getTokens();
    return tokens ? tokens.authMethod : null;
  }

  /**
   * Get user ID from stored tokens
   */
  async getStoredUserId(): Promise<string | null> {
    const tokens = await this.getTokens();
    return tokens ? tokens.userId : null;
  }

  /**
   * Check if user has active session
   */
  async hasActiveSession(): Promise<boolean> {
    return await this.hasValidTokens();
  }

  /**
   * Get session information
   */
  async getSessionInfo(): Promise<{
    isActive: boolean;
    authMethod: AuthMethod | null;
    userId: string | null;
    expiresAt: Date | null;
    timeRemaining: string;
  }> {
    const tokens = await this.getTokens();
    const validation = await this.validateTokens();

    return {
      isActive: validation.isValid,
      authMethod: tokens?.authMethod || null,
      userId: tokens?.userId || null,
      expiresAt: tokens ? new Date(tokens.expiresAt) : null,
      timeRemaining: await this.getTimeUntilExpiryFormatted()
    };
  }

  /**
   * Private: Generate a token string
   */
  private generateToken(type: 'access' | 'refresh', authMethod: AuthMethod, userId: string): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 15);
    const expiry = type === 'access' 
      ? timestamp + this.ACCESS_TOKEN_LIFETIME 
      : timestamp + this.REFRESH_TOKEN_LIFETIME;
    
    // Format: type_authMethod_userId_timestamp_expiry_random
    return `${type}_${authMethod}_${userId}_${timestamp}_${expiry}_${random}`;
  }

  /**
   * Private: Extract expiry time from token
   */
  private extractTokenExpiry(token: string): number | null {
    try {
      const parts = token.split('_');
      if (parts.length >= 5) {
        return parseInt(parts[4], 10);
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Debug: Get all token information
   */
  async getDebugInfo(): Promise<Record<string, any>> {
    const tokens = await this.getTokens();
    const validation = await this.validateTokens();
    const sessionInfo = await this.getSessionInfo();

    return {
      hasTokens: !!tokens,
      validation,
      sessionInfo,
      encryptionEnabled: this.useEncryption,
      tokens: tokens ? {
        accessToken: tokens.accessToken.substring(0, 20) + '...',
        refreshToken: tokens.refreshToken.substring(0, 20) + '...',
        authMethod: tokens.authMethod,
        userId: tokens.userId
      } : null
    };
  }
}

export default AuthTokenManager;
